/**
 * LegacySyncPanel — Live CSV API delta sync from the old Yii1 site.
 *
 * Controls:
 *   • Per-source dry-run + import buttons (accounts / transactions / orders)
 *   • Full dry-run + full import
 *   • Optional limit override for orders/transactions
 *
 * Surfaces:
 *   • API key status (CONFIGURED / NOT CONFIGURED) — never the key itself
 *   • Endpoints used (without ?key=…)
 *   • Last successful + last failed timestamp per source
 *   • Last run summary table + recent run history
 *
 * Imports go through `fetchWithStepUp` so the StepUpModal pops on the
 * server's `step_up_required` 403 challenge.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RefreshCw, PlayCircle, Database, Users, ListOrdered, Receipt,
  CheckCircle2, XCircle, Loader2, AlertTriangle, KeyRound,
  Save, Eye, EyeOff, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { fetchWithStepUp } from '../StepUpModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCES = [
  // Upstream API supports `&limit=N` on ALL endpoints. Caps
  // observed against fs.auto-remap.com on 2026-02-26:
  //   accounts     → max ≈ 5495 (the full active-customer set)
  //   transactions → max ≈ 10000
  //   orders       → max ≈ 60000+
  //   tickets      → upper bound unknown; safe ≤ 50000
  { key: 'accounts',     label: 'Accounts',     icon: <Users        className="w-4 h-4" />, hasLimit: true, maxLimit: 5500  },
  { key: 'transactions', label: 'Transactions', icon: <Receipt      className="w-4 h-4" />, hasLimit: true, maxLimit: 10000 },
  { key: 'orders',       label: 'Orders',       icon: <ListOrdered  className="w-4 h-4" />, hasLimit: true, maxLimit: 60000 },
  { key: 'tickets',      label: 'Tickets',      icon: <Receipt      className="w-4 h-4" />, hasLimit: true, maxLimit: 50000 },
];

const fmtDt = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString();
  } catch { return String(iso); }
};

const authHeader = (t) => t ? { Authorization: `Bearer ${t}` } : {};

// ─── Sub-components ────────────────────────────────────────────────

function StatusBadge({ ok, label }) {
  if (ok === null || ok === undefined) {
    return <span className="text-xs text-slate-500">{label}</span>;
  }
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
        ok ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
           : 'bg-rose-50 text-rose-700 border border-rose-200'
      }`}
      data-testid={`legacy-sync-status-${label}`}
    >
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      {label}
    </span>
  );
}

function SummaryRow({ summary }) {
  if (!summary) return null;
  const cells = [
    ['Fetched',   summary.fetched_count],
    ['Parsed',    summary.parsed_count],
    ['Created',   summary.created_count],
    ['Updated',   summary.updated_count],
    ['Unchanged', summary.unchanged_count],
    ['Skipped',   summary.skipped_count],
    ['Orphans',   summary.orphan_count],
    ['Errors',    summary.error_count],
    ['Duration',  summary.duration_ms ? `${summary.duration_ms} ms` : '—'],
  ];
  return (
    <div className="grid grid-cols-3 sm:grid-cols-9 gap-3 mt-3 text-xs">
      {cells.map(([k, v]) => (
        <div key={k} className="flex flex-col">
          <span className="uppercase tracking-wide text-[10px] text-slate-500">{k}</span>
          <span className={`font-medium ${k === 'Errors' && v > 0 ? 'text-rose-700'
                            : k === 'Orphans' && v > 0 ? 'text-amber-700'
                            : 'text-slate-800'}`}>
            {v ?? '—'}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Main panel ────────────────────────────────────────────────────
/**
 * CredentialsEditor — admin-managed editor for the two old-site
 * Legacy CSV API credentials:
 *   • X-Api-Key   header value  (`headerKey`)
 *   • ?key=       URL parameter (`queryKey`)
 *
 * Old site requires BOTH on every request. The full values are
 * NEVER pre-filled or sent back from the server — only masked
 * previews from `/legacy-sync/status` are shown as the "currently
 * configured" hint. Each field is saved independently:
 *   • leave blank + click Save on the other field → only that field
 *     is updated.
 *   • click Clear → that specific key is wiped (the other stays).
 *   • Test connection → backend hits `/accounts?limit=1` with the
 *     current credentials and reports success / HTTP error.
 *
 * Step-up MFA is required for Save and Clear.
 */
function CredentialsEditor({ token, currentAuthMode, keys, baseUrl, onSaved }) {
  const [draftHeader, setDraftHeader] = useState('');
  const [draftQuery,  setDraftQuery]  = useState('');
  const [draftBaseUrl, setDraftBaseUrl] = useState('');
  const [revealHeader, setRevealHeader] = useState(false);
  const [revealQuery,  setRevealQuery]  = useState(false);
  const [saving, setSaving]   = useState(false);
  const [clearing, setClearing] = useState(null); // 'header' | 'query' | null
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const headerMask = keys?.header;
  const queryMask  = keys?.query;
  const dualActive = currentAuthMode === 'dual';

  const post = useCallback(async (body) => {
    const r = await fetchWithStepUp(`${API_URL}/api/admin/legacy-sync/api-key`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader(token) },
      body: JSON.stringify(body),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) {
      const detail = (typeof j?.detail === 'object' ? j.detail : null) || {};
      if (detail.code === 'step_up_required' || detail.code === 'mfa_required') {
        toast.error('Admin MFA confirmation cancelled or unavailable. Click again to retry.');
      } else {
        toast.error(j?.detail || `Save failed (HTTP ${r.status})`);
      }
      return null;
    }
    return j;
  }, [token]);

  const save = useCallback(async () => {
    const h = (draftHeader || '').trim();
    const q = (draftQuery  || '').trim();
    const b = (draftBaseUrl || '').trim();
    if (!h && !q && !b) {
      toast.error('Paste at least one of the three fields before saving.');
      return;
    }
    if (b && !b.toLowerCase().startsWith('https://')) {
      toast.error('Base URL must be an HTTPS URL.');
      return;
    }
    setSaving(true);
    try {
      const body = {};
      if (h) body.header_key = h;
      if (q) body.query_key  = q;
      if (b) body.base_url   = b;
      const j = await post(body);
      if (!j) return;
      const parts = [];
      if (h) parts.push(`X-Api-Key ${j.keys?.header?.preview || '••••'}`);
      if (q) parts.push(`?key= ${j.keys?.query?.preview || '••••'}`);
      if (b) parts.push(`base_url ${j.base_url || '✓'}`);
      toast.success(`Saved: ${parts.join(', ')}. Auth mode: ${j.auth_mode}.`);
      setDraftHeader(''); setDraftQuery(''); setDraftBaseUrl('');
      setRevealHeader(false); setRevealQuery(false);
      onSaved?.();
    } finally { setSaving(false); }
  }, [draftHeader, draftQuery, draftBaseUrl, post, onSaved]);

  const clearOne = useCallback(async (which) => {
    const label = which === 'header' ? 'X-Api-Key header' : '?key= URL parameter';
    if (!window.confirm(`Clear the stored ${label}? Imports will be blocked until you replace it.`)) return;
    setClearing(which);
    try {
      const body = which === 'header' ? { header_key: '' } : { query_key: '' };
      const j = await post(body);
      if (!j) return;
      toast.success(`${label} cleared. Auth mode: ${j.auth_mode}.`);
      onSaved?.();
    } finally { setClearing(null); }
  }, [post, onSaved]);

  const testConnection = useCallback(async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/legacy-sync/test-connection`, {
        method: 'POST',
        headers: authHeader(token),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        const detail = typeof j?.detail === 'object' ? j.detail : { message: j?.detail };
        setTestResult({ ok: false, message: detail?.message || `HTTP ${r.status}`, raw: detail });
        toast.error(detail?.message || `Test failed (HTTP ${r.status})`);
        return;
      }
      setTestResult({ ok: true, sample: j.sample, row_count: j.row_count, endpoint: j.endpoint, auth_mode: j.auth_mode });
      toast.success(`Connection OK — ${j.row_count} row(s). Auth: ${j.auth_mode}.`);
    } catch (e) {
      setTestResult({ ok: false, message: e?.message || 'network error' });
      toast.error(e?.message || 'Test failed.');
    } finally { setTesting(false); }
  }, [token]);

  const renderInput = (which, draft, setDraft, reveal, setReveal, label, hint) => (
    <div className="mb-3">
      <div className="flex items-center gap-2 mb-1">
        <label className="text-xs font-medium text-slate-700">{label}</label>
        <span className="font-mono text-[11px] text-slate-500">
          {(which === 'header' ? headerMask : queryMask)?.configured
            ? `current: ${(which === 'header' ? headerMask : queryMask).preview} (length ${(which === 'header' ? headerMask : queryMask).length})`
            : 'not set'}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[260px]">
          <input
            type={reveal ? 'text' : 'password'}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={hint}
            autoComplete="off" spellCheck={false}
            data-testid={`legacy-sync-${which}-key-input`}
            className="w-full h-9 pl-3 pr-9 text-xs font-mono rounded-md border border-slate-300
                       bg-slate-50 focus:bg-white focus:border-cyan-500 focus:ring-1
                       focus:ring-cyan-500 outline-none"
          />
          <button
            type="button" onClick={() => setReveal(v => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800"
            title={reveal ? 'Hide' : 'Reveal'}
            data-testid={`legacy-sync-${which}-key-reveal-btn`}
          >
            {reveal ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
        </div>
        {(which === 'header' ? headerMask : queryMask)?.configured && (
          <button
            onClick={() => clearOne(which)}
            disabled={clearing === which}
            data-testid={`legacy-sync-${which}-key-clear-btn`}
            className="inline-flex items-center gap-1 h-9 px-2.5 text-xs
                       text-rose-700 border border-rose-200 rounded-md hover:bg-rose-50 disabled:opacity-50"
            title={`Wipe the stored ${which === 'header' ? 'X-Api-Key' : '?key='}.`}
          >
            {clearing === which ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            Clear
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="mb-4 border border-slate-200 rounded-lg p-4 bg-white">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <KeyRound className="w-4 h-4 text-slate-600" />
        <h4 className="text-sm font-semibold text-slate-900">Credentials / Security</h4>
        {dualActive ? (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 font-medium">
            dual auth active
          </span>
        ) : (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 font-medium">
            {currentAuthMode?.replace('_', ' ') || 'unconfigured'} — imports blocked
          </span>
        )}
      </div>
      <p className="text-[11px] text-slate-600 mb-3">
        Old site requires BOTH the URL <code className="px-1 py-0.5 rounded bg-slate-100 font-mono">?key=</code>
        AND the <code className="px-1 py-0.5 rounded bg-slate-100 font-mono">X-Api-Key</code> header.
        Full values are stored backend-side only; the previews here mask the keys to first 4 chars + length.
      </p>

      {/* Base URL — admin-editable, validated as HTTPS on save. */}
      <div className="mb-3" data-testid="legacy-sync-base-url-field">
        <div className="flex items-center gap-2 mb-1">
          <label className="text-xs font-medium text-slate-700">Base URL</label>
          <span className="font-mono text-[11px] text-slate-500">
            {baseUrl ? `current: ${baseUrl}` : 'not set'}
          </span>
        </div>
        <input
          type="text"
          value={draftBaseUrl}
          onChange={(e) => setDraftBaseUrl(e.target.value)}
          placeholder="https://fs.auto-remap.com/csvApi"
          autoComplete="off" spellCheck={false}
          data-testid="legacy-sync-base-url-input"
          className="w-full h-9 px-3 text-xs font-mono rounded-md border border-slate-300
                     bg-slate-50 focus:bg-white focus:border-cyan-500 focus:ring-1
                     focus:ring-cyan-500 outline-none"
        />
        <p className="mt-1 text-[10px] text-slate-500">
          Override stored in <code>app_settings.legacy_csv_base_url</code>. Must be HTTPS. Leave blank to keep current.
        </p>
      </div>

      {renderInput('header', draftHeader, setDraftHeader, revealHeader, setRevealHeader,
                    'X-Api-Key header', 'paste new X-Api-Key value…')}
      {renderInput('query',  draftQuery,  setDraftQuery,  revealQuery,  setRevealQuery,
                    'URL ?key= parameter', 'paste new ?key= value…')}
      <div className="flex flex-wrap items-center gap-2 mt-1">
        <button
          onClick={save}
          disabled={saving || (!draftHeader.trim() && !draftQuery.trim() && !draftBaseUrl.trim())}
          data-testid="legacy-sync-credentials-save-btn"
          className="inline-flex items-center gap-1 h-9 px-3 text-xs font-semibold
                     bg-cyan-600 text-white rounded-md hover:bg-cyan-700 disabled:opacity-50"
        >
          {saving
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</>
            : <><Save className="w-3.5 h-3.5" /> Save credentials</>}
        </button>
        <button
          onClick={testConnection}
          disabled={testing || !dualActive}
          data-testid="legacy-sync-test-connection-btn"
          className="inline-flex items-center gap-1 h-9 px-3 text-xs font-semibold
                     border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50 disabled:opacity-50"
          title={dualActive ? 'GET /accounts?limit=1 with the stored credentials.' : 'Both keys must be saved first.'}
        >
          {testing
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Testing…</>
            : <><PlayCircle className="w-3.5 h-3.5" /> Test connection</>}
        </button>
        {testResult && (
          <span className={
            'text-[11px] font-mono inline-flex items-center gap-1 ' +
            (testResult.ok ? 'text-emerald-700' : 'text-rose-700')
          }>
            {testResult.ok
              ? <><CheckCircle2 className="w-3.5 h-3.5" /> {`OK — ${testResult.row_count} row(s), sample id=${testResult.sample?.id ?? '—'}`}</>
              : <><XCircle className="w-3.5 h-3.5" /> {testResult.message}</>}
          </span>
        )}
      </div>
      <p className="mt-2 text-[10px] text-slate-500">
        Save / Clear triggers admin MFA step-up. Both keys are persisted to <code>app_settings</code>,
        cached in-process, and audited in <code>auth_audit_log</code> as masked previews only —
        the full values are never logged or returned.
      </p>
    </div>
  );
}




export default function LegacySyncPanel({ token }) {
  const [status,     setStatus]   = useState(null);
  const [loading,    setLoading]  = useState(true);
  const [error,      setError]    = useState('');
  const [busy,       setBusy]     = useState({});           // { 'accounts:dry':true }
  const [lastResult, setLastResult] = useState({});         // by source
  const [runs,       setRuns]     = useState([]);
  const [limits,     setLimits]   = useState({ accounts: '', transactions: '', orders: '', full: '', daily: '' });

  const fetchStatus = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/legacy-sync/status`, { headers: authHeader(token) });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setStatus(await r.json());
      setError('');
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchRuns = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/legacy-sync/runs?limit=10`,
                            { headers: authHeader(token) });
      if (r.ok) {
        const j = await r.json();
        setRuns(j.items || []);
      }
    } catch { /* non-critical */ }
  }, [token]);

  useEffect(() => { fetchStatus(); fetchRuns(); }, [fetchStatus, fetchRuns]);

  /** Poll `/runs/{run_id}` until status is no longer queued/running.
   *  Stops after ~5 min as a safety net so the spinner can never hang. */
  const pollRun = useCallback(async (runId) => {
    const start = Date.now();
    const TIMEOUT_MS = 5 * 60 * 1000;
    while (Date.now() - start < TIMEOUT_MS) {
      await new Promise(res => setTimeout(res, 2500));
      try {
        const r = await fetch(`${API_URL}/api/admin/legacy-sync/runs/${encodeURIComponent(runId)}`,
                              { headers: authHeader(token) });
        if (!r.ok) continue;
        const doc = await r.json();
        if (doc.status && !['queued', 'running'].includes(doc.status)) {
          return doc;
        }
      } catch { /* keep polling */ }
    }
    return null;
  }, [token]);

  const runSync = useCallback(async (source, mode) => {
    const key = `${source}:${mode}`;
    setBusy(b => ({ ...b, [key]: true }));
    try {
      const limit = (source === 'full' ? limits.full : limits[source]) || '';
      const qs = limit ? `?limit=${encodeURIComponent(limit)}` : '';
      const path = `/api/admin/legacy-sync/${source}/${mode === 'dry' ? 'dry-run' : 'import'}${qs}`;
      const url = `${API_URL}${path}`;
      const r = mode === 'import'
        ? await fetchWithStepUp(url, { method: 'POST', headers: authHeader(token) })
        : await fetch(url, { method: 'POST', headers: authHeader(token) });

      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        const detail = (typeof j?.detail === 'object' ? j.detail : null) || {};
        if (detail.code === 'step_up_required' || detail.code === 'mfa_required') {
          toast.error('Admin MFA confirmation cancelled or unavailable. Click Import again to retry.');
        } else if (r.status === 409) {
          toast.error(j?.detail || `${source} sync already running — wait for it to finish.`);
        } else {
          toast.error(j?.detail || `${source} ${mode} failed (HTTP ${r.status})`);
        }
        fetchStatus(); fetchRuns();
        return;
      }

      // Imports run as background tasks → poll the run-log row until
      // status flips away from queued/running, then surface the summary.
      if (mode === 'import' && j.queued && j.run_id) {
        toast.success(`${source} import queued — polling for status…`);
        const final = await pollRun(j.run_id);
        if (final) {
          setLastResult(prev => ({ ...prev, [source]: final }));
          if (final.status === 'ok') {
            toast.success(`${source} import done`);
          } else if (final.status === 'partial') {
            toast.warning(`${source} import finished with errors`);
          } else {
            toast.error(`${source} import failed`);
          }
        }
      } else {
        setLastResult(prev => ({ ...prev, [source]: j }));
        toast.success(`${source} ${mode === 'dry' ? 'dry-run' : 'import'} done`);
      }
      fetchStatus(); fetchRuns();
    } catch (e) {
      toast.error(e?.message || 'Sync failed');
    } finally {
      setBusy(b => ({ ...b, [key]: false }));
    }
  }, [token, limits, fetchStatus, fetchRuns, pollRun]);

  /** Daily catch-up — runs Accounts → Transactions → Orders sequentially,
   *  each as dry-run + import, with one MFA step-up at the start. The
   *  backend handles the actual sequencing as a single background task
   *  and emits one parent run plus sub-runs. We just poll the parent. */
  const runDailyCatchup = useCallback(async () => {
    setBusy(b => ({ ...b, 'daily:run': true }));
    try {
      const limit = limits.daily || 1000;
      const qs = `?limit=${encodeURIComponent(limit)}`;
      const url = `${API_URL}/api/admin/legacy-sync/daily-catchup${qs}`;
      const r = await fetchWithStepUp(url, { method: 'POST', headers: authHeader(token) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        const detail = (typeof j?.detail === 'object' ? j.detail : null) || {};
        if (detail.code === 'step_up_required' || detail.code === 'mfa_required') {
          toast.error('Admin MFA confirmation cancelled or unavailable. Click Daily catch-up again to retry.');
        } else if (r.status === 409) {
          toast.error(j?.detail || 'Daily catch-up already running — wait for it to finish.');
        } else {
          toast.error(j?.detail || `Daily catch-up failed (HTTP ${r.status})`);
        }
        fetchStatus(); fetchRuns();
        return;
      }
      if (j.queued && j.run_id) {
        toast.success(`Daily catch-up queued — running Accounts → Transactions → Orders → Tickets with limit ${limit}…`);
        const final = await pollRun(j.run_id);
        if (final) {
          setLastResult(prev => ({ ...prev, daily: final }));
          if (final.status === 'ok') {
            toast.success('Daily catch-up completed');
          } else if (final.status === 'partial') {
            toast.warning('Daily catch-up finished with some errors — see Recent runs');
          } else {
            toast.error(`Daily catch-up ${final.status || 'failed'}`);
          }
        }
      }
      fetchStatus(); fetchRuns();
    } catch (e) {
      toast.error(e?.message || 'Daily catch-up failed');
    } finally {
      setBusy(b => ({ ...b, 'daily:run': false }));
    }
  }, [token, limits, fetchStatus, fetchRuns, pollRun]);

  const apiOk    = status?.api?.configured;
  const keyMask  = status?.api?.key;

  if (loading) {
    return (
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 text-center text-sm text-slate-500"
               data-testid="legacy-sync-panel-loading">
        <Loader2 className="w-5 h-5 inline mr-2 animate-spin" /> Loading legacy sync status…
      </section>
    );
  }
  if (error) {
    return (
      <section className="bg-white border border-rose-200 rounded-xl shadow-sm p-6 text-sm text-rose-700"
               data-testid="legacy-sync-panel-error">
        <AlertTriangle className="w-4 h-4 inline mr-1" /> {error}
        <button onClick={fetchStatus} className="ml-3 underline">Retry</button>
      </section>
    );
  }

  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-5"
             data-testid="legacy-sync-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-slate-50 flex items-center justify-center">
            <Database className="w-5 h-5 text-slate-700" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800">Legacy CSV API — Delta sync</h3>
            <p className="text-[12px] text-slate-500">
              Pulls fresh customers, transactions and orders from the old Yii1 site's read-only CSV API.
              Imports are step-up-MFA gated.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge ok={apiOk} label={apiOk ? 'API configured' : 'API not configured'} />
          <button onClick={() => { fetchStatus(); fetchRuns(); }}
                  className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1"
                  data-testid="legacy-sync-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {/* Key + endpoints */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700 mb-4">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <KeyRound className="w-3.5 h-3.5 text-slate-500" />
          <span className="font-medium">API key:</span>
          <span data-testid="legacy-sync-key-preview" className="font-mono">
            {keyMask?.configured
              ? `${keyMask.preview} (length ${keyMask.length})`
              : 'not set'}
          </span>
          {status?.api?.auth_mode && (
            <span
              data-testid="legacy-sync-auth-mode"
              className={
                'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ' +
                (status.api.auth_mode === 'header'
                  ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                  : status.api.auth_mode === 'query_fallback'
                    ? 'bg-amber-100 text-amber-800 border border-amber-200'
                    : 'bg-slate-200 text-slate-600 border border-slate-300')
              }
              title={
                status.api.auth_mode === 'header'
                  ? 'Backend sends X-Api-Key header (preferred).'
                  : status.api.auth_mode === 'query_fallback'
                    ? 'Backend falls back to ?key=… URL parameter. Configure LEGACY_CSV_HEADER_API_KEY to switch to the header.'
                    : 'No legacy CSV API key configured.'
              }
            >
              auth: {status.api.auth_mode.replace('_', ' ')}
            </span>
          )}
          <span className="text-slate-400">— never exposed</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 text-[11px] font-mono text-slate-600 break-all">
          <div>accounts → {status?.endpoints?.accounts || '—'}</div>
          <div>transactions → {status?.endpoints?.transactions || '—'}</div>
          <div>orders → {status?.endpoints?.orders || '—'}</div>
          <div>tickets → {status?.endpoints?.tickets || '—'}</div>
        </div>
        <div className="mt-2 text-[11px] text-slate-500">
          Upstream observed caps: accounts ≤ <span className="font-mono">5,495</span> (≈ active customers),
          transactions ≤ <span className="font-mono">10,000</span>,
          orders ≤ <span className="font-mono">60,000+</span>.
          For initial catch-up, set the limit to the cap and rely on idempotency for re-runs.
        </div>
      </div>

      {/* Credentials editor (admin-managed, MFA-protected) — dual: header + query + base URL */}
      <CredentialsEditor
        token={token}
        currentAuthMode={status?.api?.auth_mode}
        keys={status?.api?.keys}
        baseUrl={status?.api?.base_url}
        onSaved={() => { fetchStatus(); }}
      />

      {/* Per-source rows */}
      <div className="space-y-3">
        {SOURCES.map(src => {
          const lastOk   = status?.last_ok?.[src.key];
          const lastFail = status?.last_failed?.[src.key];
          const result   = lastResult[src.key];
          const dryBusy  = busy[`${src.key}:dry`];
          const impBusy  = busy[`${src.key}:import`];
          return (
            <div key={src.key} className="border border-slate-200 rounded-lg p-3"
                 data-testid={`legacy-sync-source-${src.key}`}>
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center text-slate-600">
                    {src.icon}
                  </div>
                  <div>
                    <div className="font-medium text-sm text-slate-800">{src.label}</div>
                    <div className="text-[11px] text-slate-500">
                      Last OK: {fmtDt(lastOk?.started_at)}{' · '}
                      Last fail: {fmtDt(lastFail?.started_at)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {src.hasLimit && (
                    <input
                      type="number"
                      min="1"
                      max={src.maxLimit}
                      placeholder={`limit ≤ ${src.maxLimit}`}
                      value={limits[src.key]}
                      onChange={(e) => setLimits(l => ({ ...l, [src.key]: e.target.value }))}
                      className="w-28 text-xs border border-slate-300 rounded px-2 py-1"
                      data-testid={`legacy-sync-${src.key}-limit-input`}
                    />
                  )}
                  <button
                    onClick={() => runSync(src.key, 'dry')}
                    disabled={dryBusy || !apiOk}
                    className="text-xs px-3 py-1.5 rounded bg-slate-100 text-slate-800 hover:bg-slate-200 disabled:opacity-50 flex items-center gap-1"
                    data-testid={`legacy-sync-${src.key}-dry-run-btn`}
                  >
                    {dryBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
                    Dry-run
                  </button>
                  <button
                    onClick={() => runSync(src.key, 'import')}
                    disabled={impBusy || !apiOk}
                    className="text-xs px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
                    data-testid={`legacy-sync-${src.key}-import-btn`}
                  >
                    {impBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
                    Import
                  </button>
                </div>
              </div>
              {result && <SummaryRow summary={result} />}
              {Array.isArray(result?.error_samples) && result.error_samples.length > 0 && (
                <details className="mt-2 text-xs text-rose-700">
                  <summary className="cursor-pointer">{result.error_samples.length} error sample(s)</summary>
                  <pre className="bg-rose-50 border border-rose-200 rounded p-2 mt-1 overflow-x-auto">
                    {JSON.stringify(result.error_samples, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          );
        })}
      </div>

      {/* Full sync */}
      <div className="border border-slate-200 rounded-lg p-3 mt-3 bg-slate-50"
           data-testid="legacy-sync-source-full">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-white border border-slate-200 flex items-center justify-center text-slate-700">
              <Database className="w-4 h-4" />
            </div>
            <div>
              <div className="font-medium text-sm text-slate-800">Full sync (accounts → transactions → orders → tickets)</div>
              <div className="text-[11px] text-slate-500">
                Same limit value is applied to all four sub-syncs so they catch up symmetrically.
                Leave blank to use defaults (accounts=1000, txns=1000, orders=1000, tickets=1000).
                Heavy / manual operation — do not run automatically.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="1"
              max="60000"
              placeholder="limit"
              value={limits.full}
              onChange={(e) => setLimits(l => ({ ...l, full: e.target.value }))}
              className="w-28 text-xs border border-slate-300 rounded px-2 py-1"
              data-testid="legacy-sync-full-limit-input"
            />
            <button
              onClick={() => runSync('full', 'dry')}
              disabled={busy['full:dry'] || !apiOk}
              className="text-xs px-3 py-1.5 rounded bg-slate-200 text-slate-800 hover:bg-slate-300 disabled:opacity-50 flex items-center gap-1"
              data-testid="legacy-sync-full-dry-run-btn"
            >
              {busy['full:dry'] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
              Full dry-run
            </button>
            <button
              onClick={() => runSync('full', 'import')}
              disabled={busy['full:import'] || !apiOk}
              className="text-xs px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
              data-testid="legacy-sync-full-import-btn"
            >
              {busy['full:import'] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
              Full import
            </button>
          </div>
        </div>
      </div>

      {/* Daily catch-up — one-click recommended sequence */}
      <div className="border border-emerald-200 rounded-lg p-3 mt-3 bg-emerald-50"
           data-testid="legacy-sync-daily-catchup">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-white border border-emerald-200 flex items-center justify-center text-emerald-700">
              <PlayCircle className="w-4 h-4" />
            </div>
            <div>
              <div className="font-medium text-sm text-emerald-900">⭐ Daily catch-up — recommended one-click update</div>
              <div className="text-[11px] text-emerald-800/80">
                Runs <strong>Accounts → Transactions → Orders → Tickets</strong> (+ messages &amp; file metadata for tickets)
                with limit {limits.daily || 1000} each (dry-run before each import).
                Safe to re-run; imports are fully idempotent.
                Order &amp; ticket file binaries are mirrored to local GridFS lazily on first download click.
                Wallet step is <strong>read-only audit</strong> (never writes <code>users.balance</code>). Welcome-bonus
                reconciler is restricted to the 2023-03-27 20:46 Europe/Minsk cutoff rule — no synthetic +€50 for pre-cutoff users.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="1"
              max="60000"
              placeholder="limit (default 1000)"
              value={limits.daily}
              onChange={(e) => setLimits(l => ({ ...l, daily: e.target.value }))}
              className="w-36 text-xs border border-emerald-300 rounded px-2 py-1 bg-white"
              data-testid="legacy-sync-daily-limit-input"
            />
            <button
              onClick={runDailyCatchup}
              disabled={busy['daily:run'] || !apiOk}
              className="text-xs px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
              data-testid="legacy-sync-daily-catchup-btn"
            >
              {busy['daily:run'] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
              Daily catch-up
            </button>
          </div>
        </div>
        {lastResult.daily && (
          <div className="mt-2 text-[11px] text-emerald-900">
            Status: <strong>{lastResult.daily.status}</strong>
            {lastResult.daily.aborted_at && (
              <span className="text-rose-700"> · aborted at <code>{lastResult.daily.aborted_at.step}</code> ({lastResult.daily.aborted_at.reason})</span>
            )}
            {lastResult.daily.duration_ms != null && (
              <span> · {lastResult.daily.duration_ms} ms</span>
            )}
            {Array.isArray(lastResult.daily.sub_run_ids) && (
              <span> · {lastResult.daily.sub_run_ids.length} sub-runs (see Recent runs below)</span>
            )}
          </div>
        )}
      </div>

      {/* Recent runs history */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-slate-800">Recent runs</h4>
          <button onClick={fetchRuns} className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse" data-testid="legacy-sync-runs-table">
            <thead>
              <tr className="text-slate-500 text-[11px] uppercase tracking-wide">
                <th className="text-left py-1 pr-3 whitespace-nowrap">Started</th>
                <th className="text-left py-1 pr-3 whitespace-nowrap">Source</th>
                <th className="text-left py-1 pr-3 whitespace-nowrap">Mode</th>
                <th className="text-left py-1 pr-3 whitespace-nowrap">Status</th>
                <th className="text-right py-1 pr-3 whitespace-nowrap">Limit</th>
                <th className="text-right py-1 pr-3 whitespace-nowrap">Fetched</th>
                <th className="text-right py-1 pr-3 whitespace-nowrap">Created</th>
                <th className="text-right py-1 pr-3 whitespace-nowrap">Updated</th>
                <th className="text-right py-1 pr-3 whitespace-nowrap">Orphans</th>
                <th className="text-right py-1 pr-3 whitespace-nowrap">Errors</th>
                <th className="text-left py-1 pr-3 whitespace-nowrap">Legacy id range</th>
                <th className="text-left py-1 pr-3 whitespace-nowrap">Legacy date range</th>
                <th className="text-left py-1 pr-3 whitespace-nowrap">Endpoint</th>
                <th className="text-left py-1 pr-3 whitespace-nowrap">By</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 && (
                <tr><td colSpan={14} className="py-3 text-center text-slate-500">No runs yet.</td></tr>
              )}
              {runs.map((r) => {
                const idRange = (r.min_legacy_id != null || r.max_legacy_id != null)
                  ? `${r.min_legacy_id ?? '—'} … ${r.max_legacy_id ?? '—'}`
                  : '—';
                const dtRange = (r.min_legacy_date || r.max_legacy_date)
                  ? `${r.min_legacy_date || '—'} … ${r.max_legacy_date || '—'}`
                  : '—';
                return (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="py-1.5 pr-3 text-slate-700">{fmtDt(r.started_at)}</td>
                    <td className="py-1.5 pr-3">{r.source}</td>
                    <td className="py-1.5 pr-3">{r.mode}</td>
                    <td className={`py-1.5 pr-3 ${
                      r.status === 'ok' ? 'text-emerald-700'
                      : r.status === 'partial' ? 'text-amber-700'
                      : r.status === 'error' ? 'text-rose-700'
                      : 'text-slate-700'}`}>{r.status}</td>
                    <td className="py-1.5 pr-3 text-right">{r.limit_used ?? r.limit ?? '—'}</td>
                    <td className="py-1.5 pr-3 text-right">{r.fetched_count ?? '—'}</td>
                    <td className="py-1.5 pr-3 text-right">{r.created_count ?? '—'}</td>
                    <td className="py-1.5 pr-3 text-right">{r.updated_count ?? '—'}</td>
                    <td className={`py-1.5 pr-3 text-right ${r.orphan_count > 0 ? 'text-amber-700' : ''}`}>
                      {r.orphan_count ?? '—'}
                    </td>
                    <td className={`py-1.5 pr-3 text-right ${r.error_count > 0 ? 'text-rose-700' : ''}`}>
                      {r.error_count ?? '—'}
                    </td>
                    <td className="py-1.5 pr-3 text-slate-700 font-mono whitespace-nowrap">{idRange}</td>
                    <td className="py-1.5 pr-3 text-slate-700 font-mono whitespace-nowrap">{dtRange}</td>
                    <td className="py-1.5 pr-3 text-slate-500 font-mono break-all">{r.endpoint || '—'}</td>
                    <td className="py-1.5 pr-3 text-slate-500">{r.triggered_by || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
